// Signature de version posée sur chaque requête vers Supabase, dans l'en-tête
// X-Client-Info : « flowcut-ios/1.0.3 (3cfd75f) ». Le CORS des edge functions
// autorise déjà cet en-tête et les journaux Supabase le gardent déjà : rien à
// changer côté serveur.
//
// ⛔ AUCUN import dans ce fichier. Il est importé par
// src/integrations/supabase/client.ts, dont les modules importés n'importent
// rien eux-mêmes (c'est ce qui empêche un cycle).

export type ClientPlatform = "ios" | "android" | "web";

export interface ClientSignatureParts {
  platform: ClientPlatform;
  version: string;
  buildId: string;
}

const INITIAL: ClientSignatureParts = { platform: "web", version: "", buildId: "" };

let parts: ClientSignatureParts = INITIAL;
const listeners = new Set<() => void>();

// Une valeur d'en-tête ne doit porter ni saut de ligne ni séparateur.
const clean = (value: string, fallback: string): string =>
  value.replace(/[^A-Za-z0-9._-]/g, "").slice(0, 64) || fallback;

export function formatClientSignature(p: ClientSignatureParts): string {
  return `flowcut-${p.platform}/${clean(p.version, "0")} (${clean(p.buildId, "unknown")})`;
}

export function getClientSignatureParts(): ClientSignatureParts {
  return parts;
}

export function getClientSignature(): string {
  return formatClientSignature(parts);
}

export function setClientSignature(next: Partial<ClientSignatureParts>): void {
  const merged = { ...parts, ...next };
  if (formatClientSignature(merged) === formatClientSignature(parts)) return;
  parts = merged;
  listeners.forEach((listener) => listener());
}

export function subscribeClientSignature(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Enveloppe un fetch : la signature est lue au moment de CHAQUE requête. */
export function withClientSignature(fetchImpl: typeof fetch): typeof fetch {
  return (input, init) => {
    const headers = new Headers(init?.headers ?? (input instanceof Request ? input.headers : undefined));
    headers.set("X-Client-Info", getClientSignature());
    return fetchImpl(input, { ...init, headers });
  };
}

export function resetClientSignatureForTests(): void {
  parts = INITIAL;
  listeners.clear();
}
